// context/themeContext.js
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";

// Default theme configurations
const defaultLightTheme = {
  mode: "light",
  name: "Light Theme",
  background: "#ffffff",
  text: "#1a202c",
  primary: "#1976d2",
  secondary: "#f50057",
  success: "#4caf50",
  warning: "#ff9800",
  error: "#f44336",
  info: "#2196f3",
  cardBackground: "#ffffff",
  navBackground: "#ffffff",
  sidebarBackground: "#f8f9fa",
  headerBackground: "#ffffff",
  borderColor: "#e0e0e0",
  hoverColor: "#f5f5f5",
  activeColor: "#e3f2fd",
  focusColor: "#bbdefb",
  shadowColor: "rgba(0, 0, 0, 0.1)",
  overlayColor: "rgba(0, 0, 0, 0.5)",
  textSecondary: "#6c757d",
  textDisabled: "#adb5bd",
  textMuted: "#868e96",
  backgroundSecondary: "#f8f9fa",
  backgroundTertiary: "#e9ecef",
  gradient: "linear-gradient(135deg, #f0f4ff 0%, #cfe0ff 100%)",
  inputBackground: "#ffffff",
  inputBorder: "#ced4da",
  buttonDisabled: "#e9ecef",
  linkColor: "#1976d2",
};

const defaultDarkTheme = {
  mode: "dark",
  name: "Dark Theme",
  background: "#1a202c",
  text: "#f7fafc",
  primary: "#2196f3",
  secondary: "#ff4081",
  success: "#81c784",
  warning: "#ffb74d",
  error: "#ef5350",
  info: "#64b5f6",
  cardBackground: "#2d3748",
  navBackground: "linear-gradient(90deg, #fff 10%, #003a6b 90%)",
  sidebarBackground: "#2d3748",
  headerBackground: "#2d3748",
  borderColor: "#4a5568",
  hoverColor: "#374151",
  activeColor: "#4a5568",
  focusColor: "#2b6cb0",
  shadowColor: "rgba(0, 0, 0, 0.3)",
  overlayColor: "rgba(0, 0, 0, 0.7)",
  textSecondary: "#a0aec0",
  textDisabled: "#718096",
  textMuted: "#9ca3af",
  backgroundSecondary: "#2d3748",
  backgroundTertiary: "#4a5568",
  gradient: "linear-gradient(135deg, #1e243c 0%, #003a6b 100%)",
  inputBackground: "#2d3748",
  inputBorder: "#4a5568",
  buttonDisabled: "#4a5568",
  linkColor: "#2196f3",
};

// Theme presets
const themePresets = {
  light: defaultLightTheme,
  dark: defaultDarkTheme,

  ocean: {
    mode: "dark",
    name: "Ocean Blue",
    background: "#0f1419",
    text: "#e6fffa",
    primary: "#00d9ff",
    secondary: "#7c3aed",
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    info: "#06b6d4",
    cardBackground: "#1e293b",
    navBackground: "#0f172a",
    sidebarBackground: "#1e293b",
    headerBackground: "#1e293b",
    borderColor: "#334155",
    hoverColor: "#334155",
    activeColor: "#475569",
    focusColor: "#0891b2",
    shadowColor: "rgba(0, 217, 255, 0.2)",
    overlayColor: "rgba(15, 20, 25, 0.8)",
    textSecondary: "#94a3b8",
    textDisabled: "#64748b",
    textMuted: "#475569",
    backgroundSecondary: "#1e293b",
    backgroundTertiary: "#334155",
    gradient: "linear-gradient(135deg, #0f1419 0%, #1e3a8a 100%)",
    inputBackground: "#1e293b",
    inputBorder: "#334155",
    buttonDisabled: "#475569",
    linkColor: "#00d9ff",
  },

  sunset: {
    mode: "light",
    name: "Sunset Orange",
    background: "#fef7f0",
    text: "#7c2d12",
    primary: "#ea580c",
    secondary: "#dc2626",
    success: "#16a34a",
    warning: "#d97706",
    error: "#dc2626",
    info: "#0284c7",
    cardBackground: "#fff7ed",
    navBackground: "#fff7ed",
    sidebarBackground: "#fef3c7",
    headerBackground: "#fff7ed",
    borderColor: "#fed7aa",
    hoverColor: "#fef3c7",
    activeColor: "#fde68a",
    focusColor: "#f59e0b",
    shadowColor: "rgba(234, 88, 12, 0.1)",
    overlayColor: "rgba(124, 45, 18, 0.5)",
    textSecondary: "#a16207",
    textDisabled: "#d97706",
    textMuted: "#92400e",
    backgroundSecondary: "#fef3c7",
    backgroundTertiary: "#fde68a",
    gradient: "linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)",
    inputBackground: "#fff7ed",
    inputBorder: "#fed7aa",
    buttonDisabled: "#fde68a",
    linkColor: "#ea580c",
  },

  forest: {
    mode: "dark",
    name: "Forest Green",
    background: "#0f1b0f",
    text: "#f0fdf4",
    primary: "#22c55e",
    secondary: "#84cc16",
    success: "#10b981",
    warning: "#eab308",
    error: "#ef4444",
    info: "#06b6d4",
    cardBackground: "#1c2e1c",
    navBackground: "#0f1b0f",
    sidebarBackground: "#1c2e1c",
    headerBackground: "#1c2e1c",
    borderColor: "#365a36",
    hoverColor: "#22543d",
    activeColor: "#2f855a",
    focusColor: "#38a169",
    shadowColor: "rgba(34, 197, 94, 0.2)",
    overlayColor: "rgba(15, 27, 15, 0.8)",
    textSecondary: "#a7f3d0",
    textDisabled: "#6ee7b7",
    textMuted: "#86efac",
    backgroundSecondary: "#1c2e1c",
    backgroundTertiary: "#22543d",
    gradient: "linear-gradient(135deg, #0f1b0f 0%, #14532d 100%)",
    inputBackground: "#1c2e1c",
    inputBorder: "#365a36",
    buttonDisabled: "#22543d",
    linkColor: "#22c55e",
  },

  purple: {
    mode: "dark",
    name: "Royal Purple",
    background: "#1e0a2e",
    text: "#faf5ff",
    primary: "#8b5cf6",
    secondary: "#ec4899",
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    info: "#06b6d4",
    cardBackground: "#2d1b4e",
    navBackground: "#1e0a2e",
    sidebarBackground: "#2d1b4e",
    headerBackground: "#2d1b4e",
    borderColor: "#553c9a",
    hoverColor: "#44337a",
    activeColor: "#6d28d9",
    focusColor: "#7c3aed",
    shadowColor: "rgba(139, 92, 246, 0.2)",
    overlayColor: "rgba(30, 10, 46, 0.8)",
    textSecondary: "#c4b5fd",
    textDisabled: "#a78bfa",
    textMuted: "#8b5cf6",
    backgroundSecondary: "#2d1b4e",
    backgroundTertiary: "#44337a",
    gradient: "linear-gradient(135deg, #1e0a2e 0%, #581c87 100%)",
    inputBackground: "#2d1b4e",
    inputBorder: "#553c9a",
    buttonDisabled: "#44337a",
    linkColor: "#8b5cf6",
  },

  corporate: {
    mode: "light",
    name: "Corporate Blue",
    background: "#f8fafc",
    text: "#1e293b",
    primary: "#0f172a",
    secondary: "#3b82f6",
    success: "#059669",
    warning: "#d97706",
    error: "#dc2626",
    info: "#0284c7",
    cardBackground: "#ffffff",
    navBackground: "#ffffff",
    sidebarBackground: "#f1f5f9",
    headerBackground: "#ffffff",
    borderColor: "#e2e8f0",
    hoverColor: "#f1f5f9",
    activeColor: "#e2e8f0",
    focusColor: "#3b82f6",
    shadowColor: "rgba(15, 23, 42, 0.1)",
    overlayColor: "rgba(30, 41, 59, 0.5)",
    textSecondary: "#64748b",
    textDisabled: "#94a3b8",
    textMuted: "#475569",
    backgroundSecondary: "#f1f5f9",
    backgroundTertiary: "#e2e8f0",
    gradient: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
    inputBackground: "#ffffff",
    inputBorder: "#e2e8f0",
    buttonDisabled: "#e2e8f0",
    linkColor: "#0f172a",
  },
};
const ThemeContext = createContext(undefined);

// Theme Provider Component
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(defaultDarkTheme);

  // Apply CSS custom properties to document root
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(theme).forEach(([key, value]) => {
      if (typeof value === "string" && key !== "name" && key !== "mode") {
        root.style.setProperty(
          `--theme-${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`,
          value
        );
      }
    });
  }, [theme]);

  // Toggle between light and dark themes
  const toggleTheme = useCallback(() => {
    setTheme((prevTheme) =>
      prevTheme.mode === "light" ? defaultDarkTheme : defaultLightTheme
    );
  }, []);

  // Apply a preset theme
  const applyPreset = useCallback((presetName) => {
    if (themePresets[presetName]) {
      setTheme(themePresets[presetName]);
    } else {
      console.warn(`Theme preset "${presetName}" not found`);
    }
  }, []);

  // Update a single theme property
  const updateThemeProperty = useCallback((property, value) => {
    setTheme((prevTheme) => ({
      ...prevTheme,
      [property]: value,
    }));
  }, []);

  // Reset theme to default
  const resetToDefault = useCallback(() => {
    const defaultTheme =
      theme.mode === "dark" ? defaultDarkTheme : defaultLightTheme;
    setTheme(defaultTheme);
  }, [theme.mode]);

  // Generate gradient from two colors
  const generateGradient = useCallback(
    (color1, color2, direction = "135deg") => {
      return `linear-gradient(${direction}, ${color1} 0%, ${color2} 100%)`;
    },
    []
  );

  // Context value
  const value = {
    theme,
    setTheme,
    toggleTheme,
    applyPreset,
    updateThemeProperty,
    resetToDefault,
    generateGradient,
    themePresets,
    currentThemeName: theme.name || "Custom",
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
};

// Export defaults for external use
export { themePresets, defaultLightTheme, defaultDarkTheme };
